import type { Node, Edge } from "@xyflow/react";
import { NodeData, ResponseType } from "../types/types";
import { XMLParser } from "fast-xml-parser";
import { nanoid } from "nanoid";

interface OPMLOutline {
  "@_text"?: string;
  "@_note"?: string;
  "@_color"?: string;
  "@_backgroundColor"?: string;
  "@_created"?: string;
  "@_modified"?: string;
  "@_complete"?: string;
  "@_priority"?: string;
  "@_status"?: string;
  note?: string | { "#text"?: string };
  outline?: OPMLOutline | OPMLOutline[];
}

interface OPMLDocument {
  opml?: {
    "@_version"?: string;
    head?: {
      title?: string;
      dateCreated?: string;
      dateModified?: string;
    };
    body?: {
      outline?: OPMLOutline | OPMLOutline[];
    };
  };
}

export const handleImportMindManager = async (
  file: File,
): Promise<ResponseType & { nodes?: Node<NodeData>[]; edges?: Edge[] }> => {
  try {
    // Read file content
    const text = await file.text();

    // Parse XML
    const parser = new XMLParser({
      ignoreAttributes: false,
      attributeNamePrefix: "@_",
      textNodeName: "#text",
      parseAttributeValue: true,
      trimValues: true,
    });

    const parsed = parser.parse(text) as OPMLDocument;

    if (!parsed.opml?.body?.outline) {
      return {
        success: false,
        description: "Invalid OPML file format: No outline found.",
      };
    }

    const nodes: Node<NodeData>[] = [];
    const edges: Edge[] = [];

    // Helper function to parse an outline recursively
    const parseOutline = (
      outline: OPMLOutline,
      parentId: string | null,
      position: { x: number; y: number },
      level: number = 0,
    ): void => {
      const nodeId = nanoid();

      // Extract label
      const label = outline["@_text"] || "Untitled";

      // Extract notes - check both attribute and element formats
      let notes = outline["@_note"] || "";
      if (!notes && outline.note) {
        if (typeof outline.note === "string") {
          notes = outline.note;
        } else if (outline.note["#text"]) {
          notes = outline.note["#text"];
        }
      }

      // Extract task information
      const complete =
        outline["@_complete"] === "true" || outline["@_complete"] === "100";
      const status = outline["@_status"] || "none";
      const priority = outline["@_priority"] || "";

      // Extract colors
      const color = outline["@_color"] || "";
      const backgroundColor = outline["@_backgroundColor"] || "";

      // Extract dates if present
      const taskStartDate = outline["@_created"] || "";
      const taskEndDate = outline["@_modified"] || "";

      // Create node
      const node: Node<NodeData> = {
        id: nodeId,
        type: level === 0 ? "center" : "mindmap",
        position: position,
        data: {
          label,
          icon: "",
          object: "",
          objectType: "none",
          objectWidth: 0,
          objectHeight: 0,
          notes,
          complete,
          status,
          flagged: "none",
          color,
          backgroundColor,
          presentationOrder: "0",
          priority,
          value: "",
          links: undefined,
          values: [],
          rollupValues: [],
          customProperties: [],
          event: undefined,
          address: undefined,
          ms365Document: undefined,
          task:
            taskStartDate || taskEndDate || priority
              ? {
                  id: nodeId,
                  nodeTitle: label,
                  title: label,
                  status,
                  priority,
                  startDate: taskStartDate,
                  endDate: taskEndDate,
                }
              : undefined,
        },
        parentId: parentId || undefined,
      };

      nodes.push(node);

      // Create edge if there's a parent
      if (parentId) {
        edges.push({
          id: `e-${parentId}-${nodeId}`,
          source: parentId,
          target: nodeId,
          type: "mindmap",
        });
      }

      // Parse child outlines
      if (outline.outline) {
        const children = Array.isArray(outline.outline)
          ? outline.outline
          : [outline.outline];

        const childSpacing = 50;
        const startY = position.y - ((children.length - 1) * childSpacing) / 2;

        children.forEach((child, index) => {
          const childPosition = {
            x: position.x + 150,
            y: startY + index * childSpacing,
          };
          parseOutline(child, nodeId, childPosition, level + 1);
        });
      }
    };

    // Start parsing - handle single or multiple root outlines
    const rootOutlines = Array.isArray(parsed.opml.body.outline)
      ? parsed.opml.body.outline
      : [parsed.opml.body.outline];

    // If there's only one root outline, treat it as center
    if (rootOutlines.length === 1) {
      parseOutline(rootOutlines[0], null, { x: 0, y: 0 }, 0);
    } else {
      // Multiple roots - create a virtual center node
      const centerNodeId = nanoid();
      const title = parsed.opml.head?.title || "Imported Map";

      const centerNode: Node<NodeData> = {
        id: centerNodeId,
        type: "center",
        position: { x: 0, y: 0 },
        data: {
          label: title,
          icon: "",
          object: "",
          objectType: "none",
          objectWidth: 0,
          objectHeight: 0,
          notes: "",
          complete: false,
          status: "none",
          flagged: "none",
          color: "",
          backgroundColor: "",
          presentationOrder: "0",
          priority: "",
          value: "",
          links: undefined,
          values: [],
          rollupValues: [],
          customProperties: [],
          event: undefined,
          address: undefined,
          ms365Document: undefined,
          task: undefined,
        },
      };

      nodes.push(centerNode);

      const childSpacing = 150;
      const startY = -((rootOutlines.length - 1) * childSpacing) / 2;

      rootOutlines.forEach((rootOutline, index) => {
        const childPosition = {
          x: 250,
          y: startY + index * childSpacing,
        };
        parseOutline(rootOutline, centerNodeId, childPosition, 1);
      });
    }

    return {
      success: true,
      description: `Successfully imported ${nodes.length} nodes from OPML file.`,
      nodes,
      edges,
    };
  } catch (error) {
    console.error("Error importing OPML file:", error);
    return {
      success: false,
      description: `Error importing OPML file: ${(error as Error).message}`,
    };
  }
};
